/**
 * simple-dropdown-cell — a plain option-list picker for glide-data-grid.
 *
 * Replaces @glideapps/glide-data-grid-cells' dropdown-cell, which mounts a
 * react-select searchable combobox (heavy, ~450ms to paint inside the portal,
 * and its styles don't survive there). Our option sets are short and fixed per
 * event, so a static vertical list of clickable rows is the right control.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  type CustomCell,
  type CustomRenderer,
  GridCellKind,
  type ProvideEditorCallback,
} from "@glideapps/glide-data-grid";

export interface SimpleDropdownCellProps {
  readonly kind: "simple-dropdown-cell";
  readonly value: string;
  readonly allowedValues: readonly string[];
}

export type SimpleDropdownCell = CustomCell<SimpleDropdownCellProps>;

const Editor: ReturnType<ProvideEditorCallback<SimpleDropdownCell>> = (p) => {
  const { value: cell, onChange, onFinishedEditing } = p as any;
  const { value, allowedValues } = cell.data as SimpleDropdownCellProps;

  const opts = useMemo(() => {
    const list = [...(allowedValues ?? [])];
    // Always offer a way back to empty.
    if (!list.includes("")) list.unshift("");
    return list;
  }, [allowedValues]);

  const [active, setActive] = useState(() => {
    const i = opts.indexOf(value ?? "");
    return i < 0 ? 0 : i;
  });
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    listRef.current?.focus();
  }, []);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const commit = (v: string) => {
    const next = { ...cell, data: { ...(cell.data as object), value: v } };
    onChange(next);
    onFinishedEditing(next);
  };

  return (
    <div
      ref={listRef}
      tabIndex={0}
      role="listbox"
      onKeyDown={(e) => {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          e.stopPropagation();
          setActive((i) => Math.min(opts.length - 1, i + 1));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          e.stopPropagation();
          setActive((i) => Math.max(0, i - 1));
        } else if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          commit(opts[active] ?? "");
        } else if (e.key === "Escape") {
          e.stopPropagation();
          onFinishedEditing(undefined);
        }
      }}
      style={{
        maxHeight: 260,
        overflowY: "auto",
        outline: "none",
        padding: 4,
        minWidth: 160,
        fontSize: 13,
        fontFamily: "inherit",
      }}
    >
      {opts.map((o, i) => {
        const selected = (value ?? "") === o;
        return (
          <div
            key={o || "__empty"}
            data-idx={i}
            role="option"
            aria-selected={selected}
            onMouseEnter={() => setActive(i)}
            onMouseDown={(e) => {
              e.preventDefault();
              commit(o);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 8px",
              borderRadius: 4,
              cursor: "pointer",
              background: i === active ? "#eef2ff" : "transparent",
              color: o === "" ? "#94a3b8" : "#0f172a",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ width: 12, color: "#2563eb" }}>{selected ? "✓" : ""}</span>
            <span>{o === "" ? "—" : o}</span>
          </div>
        );
      })}
    </div>
  );
};

export const simpleDropdownCellRenderer: CustomRenderer<SimpleDropdownCell> = {
  kind: GridCellKind.Custom,
  isMatch: (c): c is SimpleDropdownCell =>
    (c.data as any)?.kind === "simple-dropdown-cell",
  draw: (args, cell) => {
    const { ctx, rect, theme } = args;
    const v = cell.data.value ?? "";
    ctx.save();
    ctx.fillStyle = theme.textDark;
    ctx.textBaseline = "middle";
    ctx.fillText(
      v,
      rect.x + theme.cellHorizontalPadding,
      rect.y + rect.height / 2 + 0.5
    );
    ctx.restore();
    return true;
  },
  measure: (ctx, cell) => ctx.measureText(cell.data.value ?? "").width + 32,
  provideEditor: () => ({
    editor: Editor,
    disablePadding: true,
    deletedValue: (v) => ({ ...v, data: { ...v.data, value: "" } }),
  }),
  onPaste: (v, d) => ({ ...d, value: v }),
};

export default simpleDropdownCellRenderer;
